import { layoutSizes } from '@/theme/layoutSizes';
import { s, type SpacingToken } from '@/theme/tokens';
import { layoutModeTokens, useLayoutMode } from '@/ui/layouts/layoutMode';
import { Box } from './Box';
import { Container } from './Container';
import type { ReactElement, ReactNode } from 'react';

const SPACING_MAP: Record<SpacingToken, string> = {
  none: s('none'),
  xxxs: s('xxxs'),
  xxs: s('xxs'),
  xs: s('xs'),
  sm: s('sm'),
  md: s('md'),
  lg: s('lg'),
  xl: s('xl'),
  '2xl': s('2xl'),
  '3xl': s('3xl'),
  '4xl': s('4xl'),
  '5xl': s('5xl'),
  gutter: s('gutter'),
  section: s('section'),
};

const resolveSpacing = (token: SpacingToken | undefined): string | number | undefined =>
  token ? SPACING_MAP[token] : undefined;

const resolveTopSpacing = (
  paddingTop: SpacingToken | undefined,
  paddingY: SpacingToken | undefined,
): string | number | undefined => {
  if (paddingTop) return resolveSpacing(paddingTop);
  if (!paddingY) return undefined;
  if (paddingY === 'none') return SPACING_MAP.none;
  return s('md');
};

type PageShellProps = {
  children: ReactNode;
  maxWidth?: false | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  paddingX?: SpacingToken;
  paddingY?: SpacingToken;
  paddingTop?: SpacingToken;
  paddingBottom?: SpacingToken;
};

export const PageShell = ({
  children,
  maxWidth = false,
  paddingX = 'none',
  paddingY = 'none',
  paddingTop,
  paddingBottom = 'md',
}: PageShellProps): ReactElement => {
  const layoutMode = useLayoutMode();
  const clampXlWidth = maxWidth === 'xl';
  const resolvedMaxWidth = clampXlWidth ? false : maxWidth;
  const compactPaddingX: SpacingToken =
    paddingX === 'none' ||
    paddingX === 'xxxs' ||
    paddingX === 'xxs' ||
    paddingX === 'xs' ||
    paddingX === 'sm' ||
    paddingX === 'md'
      ? paddingX
      : 'lg';
  const capWidePaddingX =
    clampXlWidth &&
    (paddingX === 'lg' ||
      paddingX === 'xl' ||
      paddingX === '2xl' ||
      paddingX === '3xl' ||
      paddingX === '4xl' ||
      paddingX === '5xl' ||
      paddingX === 'gutter' ||
      paddingX === 'section');
  const resolvedPaddingX =
    layoutMode === layoutModeTokens.wide ? (capWidePaddingX ? 'xl' : paddingX) : compactPaddingX;
  const boostedPaddingX: SpacingToken = resolvedPaddingX === 'lg' ? 'xl' : resolvedPaddingX;
  const finalPaddingX: SpacingToken =
    layoutMode === layoutModeTokens.medium && boostedPaddingX === 'xl' ? 'lg' : boostedPaddingX;
  const contentMinHeight = paddingTop === 'xl' ? 1080 : undefined;

  return (
    <Container
      maxWidth={resolvedMaxWidth}
      disableGutters
      sx={{
        px: resolveSpacing(finalPaddingX),
        pt: resolveTopSpacing(paddingTop, paddingY),
        pb: resolveSpacing(paddingBottom),
        maxWidth: clampXlWidth ? layoutSizes.content.maxWidth : undefined,
        width: '100%',
        mx: clampXlWidth ? 'auto' : undefined,
        minWidth: 0,
      }}
    >
      <Box sx={{ minWidth: 0, minHeight: contentMinHeight }}>{children}</Box>
    </Container>
  );
};
